import { Fragment, useEffect } from 'react';

import { useDroppable } from '@dnd-kit/core';
import { AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

import { BasicFade } from '@components/animators/fade';
import { BlueLine } from '@components/drag-and-drop/defaults';
import { EmptyState } from '@components/empty-state';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { useScopedTranslate } from '@locale/translate';
import { scrollIntoView } from '@utils/browser';
import { pxToRem } from '@utils/converters';

import { useFormBuilderContext } from './context';
import { DropLine } from './drop-line';
import { getFieldConfigForKind } from './field-registry';
import { StateField } from './state';

export const EXTRA_DROPZONE_ID = `extra-bottom-dropzone`;

export const RenderField = ({ field }: { field: StateField }) => {
  try {
    const { Component } = getFieldConfigForKind(field.kind);
    return <Component field={field} />;
  } catch (e) {
    return null;
  }
};

const useScrollSelectedIntoView = () => {
  const {
    store: { useSelectedField, actions },
  } = useFormBuilderContext();
  const selectedField = useSelectedField();

  useEffect(() => {
    if (!selectedField) return;

    const to = setTimeout(() => {
      const listElement = document.getElementById(`field-${selectedField.id}`);
      if (!listElement) return;

      const { top, bottom } = listElement.getBoundingClientRect();

      // Make sure the field is fully in view
      const inView = top >= 0 && top <= window.innerHeight && bottom <= window.innerHeight;

      // We want to scroll the selected element into view, UNLESS:
      if (
        // The element is already in view
        inView ||
        // The currently focused element is an input. F.ex. if the editing
        // container autofocuses an input inside it as soon as it opens.
        // In that case we don't want to try to scroll into view as it will
        // cause a jitter in the scrolling animation
        document.activeElement?.tagName === 'INPUT'
      ) {
        return;
      }

      scrollIntoView(listElement);
    });

    return () => {
      clearTimeout(to);
    };
  }, [selectedField, actions]);
};

export const FieldList = () => {
  const {
    store: { useFormRootFields },
  } = useFormBuilderContext();
  const rootFields = useFormRootFields();

  useScrollSelectedIntoView();

  const dropzone = useDroppable({
    id: EXTRA_DROPZONE_ID,
  });
  const lastField = rootFields.length > 0 ? rootFields[rootFields.length - 1] : undefined;
  const isLastSubmit = lastField ? lastField.kind === 'submit' : false;
  const isLastActive = dropzone.active?.data.current?.field?.id === lastField?.id;
  const showExtraDropzone = rootFields.length > 0 && !isLastSubmit && !isLastActive;

  return (
    <AnimatePresence initial={false}>
      <div id="field-list" data-c="field-list">
        {rootFields.map((field) => {
          if (field.kind === 'submit') {
            return (
              <Fragment key={field.id}>
                <FirstFieldPointer />
                <RenderField field={field} key={field.id} />
              </Fragment>
            );
          }
          return <RenderField field={field} key={field.id} />;
        })}
        {showExtraDropzone && (
          <ExtraDropzone ref={dropzone.setNodeRef}>{dropzone.isOver && <BlueLine />}</ExtraDropzone>
        )}
      </div>
    </AnimatePresence>
  );
};

const ExtraDropzone = styled.div({
  position: 'relative',
  width: '100%',
  height: pxToRem(40),
});

const FirstFieldPointer = () => {
  const {
    store: { useIsNewAndUntouched, useFormType },
  } = useFormBuilderContext();
  const showPointer = useIsNewAndUntouched();
  const { t } = useScopedTranslate(locale);
  const { isOver, setNodeRef } = useDroppable({
    id: 'first-insert',
  });

  const formType = useFormType();

  return formType === 'form' ? (
    <AnimatePresence initial={false}>
      {showPointer && (
        <PointerFade>
          {isOver && <DropLine />}
          <EmptyContainer ref={setNodeRef}>
            <EmptyState
              variant="small"
              iconShadow={<IconArrowLeft size={110} />}
              icon={<IconArrowLeft size={40} />}
              header={t('header')}
              content={t('content')}
            />
          </EmptyContainer>
        </PointerFade>
      )}
    </AnimatePresence>
  ) : null;
};

const PointerFade = styled(BasicFade)({
  margin: `${pxToRem(-16)} 0`,
});

const EmptyContainer = styled.div({
  marginTop: pxToRem(24),
  marginLeft: pxToRem(-16),
});

const locale = {
  en: {
    content: 'Add and manage your form content from the menu on the left',
    add: 'Add',
    header: 'Empty form',
  },
};
